import React, { memo, useState, useRef } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel } from '../pulseUtils';
import {
  Sparkles,
  Bot,
  Mic,
  Globe,
  Cpu,
  Sliders,
  ShieldCheck,
  Zap,
  BookOpen,
  CheckCircle2,
  Activity,
  Layers,
  Lock,
  Database,
  ArrowRight,
  ExternalLink,
  UserCheck,
  AlertTriangle,
  Headphones,
  PhoneCall,
  Crown,
  Upload,
  Loader2,
  Image as ImageIcon,
  Eye,
  RefreshCw,
  Maximize2,
  Trash2,
  Code2,
} from 'lucide-react';
import { LunaFace, LUNA_FACE_CATALOG } from '../../LunaFace';
import { BespokeAvatarRequestModal } from '../modals/BespokeAvatarRequestModal';
import { LunaCacheInspectionModal } from '../modals/LunaCacheInspectionModal';

export interface LunaCoreNodeData {
  agentName?: string;
  customDeveloperName?: string;
  avatarFaceId?: string;
  customAvatarUrl?: string;
  customAvatarName?: string;
  baseVoice?: string;
  languageMode?: string;
  temperature?: number;
  naturalSpokenSinhala?: boolean;
  coreDirectives?: string;
  isActive?: boolean;
  enableHumanHandoverAlert?: boolean;
  ownerAlertPhone?: string;
  muteOnTakeover?: boolean;
  isCustomizable?: boolean;
  isNeuralCacheEnabled?: boolean;
  [key: string]: unknown;
}

const BASE_VOICES = [
  { id: 'Kore', name: 'Kore (Firm & Balanced)', gender: 'Female', badge: 'DEFAULT' },
  { id: 'Aoede', name: 'Aoede (Warm & Empathetic)', gender: 'Female', badge: 'CONCIERGE' },
  { id: 'Puck', name: 'Puck (Energetic & Playful)', gender: 'Male', badge: 'RETAIL' },
  { id: 'Fenrir', name: 'Fenrir (Deep & Authoritative)', gender: 'Male', badge: 'ENTERPRISE' },
  { id: 'Charon', name: 'Charon (Calm & Professional)', gender: 'Male', badge: 'FINANCE' },
  { id: 'Zephyr', name: 'Zephyr (Light & Friendly)', gender: 'Female', badge: 'MODERN' },
];

const LANGUAGE_MODES = [
  { id: 'bilingual', name: 'Bilingual (Sinhala & English) & Multilingual Auto-Detect (First Priority: Sinhala & English)', desc: 'Full auto-switch, cross-lingual RAG & any language support with primary priority to Sinhala & English' },
  { id: 'english', name: 'English Only', desc: 'Strict English responses only' },
  { id: 'sinhala', name: 'Sinhala Only (සිංහල)', desc: 'Strict Sinhala voice & chat responses' },
  { id: 'multilingual', name: 'Multilingual (Global)', desc: 'Auto-detect any language spoken globally' },
];

export const LunaCoreNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges } = useReactFlow();
  const nodeData = (data || {}) as LunaCoreNodeData;
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isCacheModalOpen, setIsCacheModalOpen] = useState(false);
  const [isPurgingCache, setIsPurgingCache] = useState(false);
  const [cachePurgeToast, setCachePurgeToast] = useState<string | null>(null);
  const [modalDefaultTab, setModalDefaultTab] = useState<'bespoke_avatar' | 'white_label_rename' | 'full_package'>('bespoke_avatar');
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarUploadError, setAvatarUploadError] = useState<string | null>(null);
  const [isTogglingCache, setIsTogglingCache] = useState(false);
  const debounceTimerRef = useRef<any>(null);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);

  const { isTenantView, isWhiteLabelEnabled, customAvatarUrl, customAvatarName, tenant, authToken, tenantApiKey, activeNodeIds, pulseState, openPortalData, openAccountModal, isCompactView, openInspector } = useTenantView();
  const isWhiteLabelActive = isWhiteLabelEnabled === true || tenant?.isWhiteLabelEnabled === true;
  const isCustomizable = nodeData.isCustomizable !== false;
  const canRenameAgent = !isTenantView || isWhiteLabelActive || isCustomizable;
  const canUploadAvatar = !isTenantView || isWhiteLabelActive || isCustomizable;

  const handleQuickPurgeCache = async () => {
    setIsPurgingCache(true);
    setCachePurgeToast(null);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;

      const res = await fetch('/api/admin/luna-flows/purge-cache', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          tenantId: tenant?.id || 'global',
          channel: 'default',
        }),
      });
      const data = await res.json();
      if (data.success) {
        setCachePurgeToast('Re-indexed!');
        setTimeout(() => setCachePurgeToast(null), 3000);
      }
    } catch (err) {
      console.warn('[LunaCoreNode] Quick purge error:', err);
    } finally {
      setIsPurgingCache(false);
    }
  };

  // ── WhatsApp Chats & Memory Purge Engine ──
  const [showDeleteChatsModal, setShowDeleteChatsModal] = useState<boolean>(false);
  const [isClearingChats, setIsClearingChats] = useState<boolean>(false);
  const [clearChatStatus, setClearChatStatus] = useState<string | null>(null);

  const handleClearAllWhatsAppChats = async () => {
    if (isClearingChats) return;
    setIsClearingChats(true);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;

      const res = await fetch('/api/tenet/live-chats/delete-all', {
        method: 'POST',
        headers,
        body: JSON.stringify({ channel: 'whatsapp' }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setClearChatStatus(`✅ Deleted ${data.deletedCount || 0} chats. Luna context reset!`);
        setTimeout(() => setClearChatStatus(null), 5000);
        setShowDeleteChatsModal(false);
      } else {
        alert(data.error || 'Failed to clear WhatsApp chats');
      }
    } catch (err: any) {
      console.error('[LunaCoreNode] Error clearing chats:', err);
      alert('Network error clearing WhatsApp chats');
    } finally {
      setIsClearingChats(false);
    }
  };

  const isNeuralCacheEnabled = nodeData.isNeuralCacheEnabled !== false;

  const handleToggleNeuralCache = async () => {
    const nextState = !isNeuralCacheEnabled;
    setIsTogglingCache(true);
    handleChange('isNeuralCacheEnabled', nextState);

    try {
      const endpoint = isTenantView ? '/api/tenet/luna-core/toggle-cache' : '/api/admin/luna-flows/toggle-cache';
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;

      await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          tenantId: tenant?.id || 'global',
          enabled: nextState,
        }),
      });
    } catch (err) {
      console.warn('[LunaCoreNode] Toggle cache error:', err);
    } finally {
      setIsTogglingCache(false);
    }
  };

  const handleAvatarFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingAvatar(true);
    setAvatarUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers: Record<string, string> = {};
      const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;

      const origin = typeof window !== 'undefined' ? window.location.origin : 'https://kangavena.com';
      const res = await fetch('/api/admin/luna-flows/upload-asset', {
        method: 'POST',
        headers,
        body: formData,
      });
      const resData = await res.json();
      if (resData.success && resData.url) {
        const fullUrl = resData.url.startsWith('http') ? resData.url : `${origin}${resData.url}`;
        setNodes((nds) =>
          nds.map((n) => (n.id === id ? {
            ...n,
            data: {
              ...n.data,
              customAvatarUrl: fullUrl,
              avatarFaceId: 'custom_avatar',
              customAvatarName: (n.data as any)?.agentName || 'My Brand Avatar',
            }
          } : n))
        );
        persistNodeDataDirectly('customAvatarUrl', fullUrl, {
          avatarFaceId: 'custom_avatar',
          customAvatarName: nodeData.agentName || 'My Brand Avatar',
        });
      } else {
        setAvatarUploadError(resData.error || 'Upload failed');
      }
    } catch (err: any) {
      setAvatarUploadError(err?.message || 'Network error during upload');
    } finally {
      setIsUploadingAvatar(false);
      if (avatarFileInputRef.current) avatarFileInputRef.current.value = '';
    }
  };

  const persistNodeDataDirectly = (field: keyof LunaCoreNodeData, value: any, allUpdates?: Partial<LunaCoreNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'LUNA_CORE_HUB',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[LunaCoreNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleChange = (field: keyof LunaCoreNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly(field, value);
  };

  const currentFaceId = nodeData.avatarFaceId || 'cute_chibi_bot';
  const currentVoice = nodeData.baseVoice || 'Kore';
  const currentLang = nodeData.languageMode || 'bilingual';
  const currentTemp = typeof nodeData.temperature === 'number' ? nodeData.temperature : 0.7;
  const currentName = canRenameAgent ? (nodeData.agentName || customAvatarName || 'Luna AI') : 'Luna AI';
  
  const pulseLevel = getNodePulseLevel(id, pulseState, activeNodeIds);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';

  const isCustomFace = currentFaceId === 'custom_avatar' || currentFaceId === 'custom_photo' || currentFaceId === 'custom_bespoke_3d' || currentFaceId === 'custom_saara_teacher';
  const activeFaceObj = isCustomFace
    ? { id: 'custom_avatar', name: 'Custom Brand Avatar', tagline: 'Custom uploaded brand photo / logo', badge: 'CUSTOM' }
    : (LUNA_FACE_CATALOG.find((f) => f.id === currentFaceId) || LUNA_FACE_CATALOG[0]);

  return (
    <div
      className={`rounded-3xl bg-gradient-to-b from-[#0B0F1E]/98 via-[#070A14]/98 to-[#04060C]/98 backdrop-blur-2xl border transition-all duration-300 w-[440px] relative select-none group shadow-[0_20px_50px_rgba(0,0,0,0.9),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive 
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-cyan-400 ring-4 ring-cyan-400/30 shadow-[0_0_50px_rgba(6,182,212,0.5),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : 'border-cyan-500/40 hover:border-cyan-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_25px_rgba(6,182,212,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
      }`}
    >
      {/* ── Left Side: Multi-Port Input Handles ──────────────────────── */}
      {/* Universal Target Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-core"
        className="!bg-cyan-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 top-1/2 -translate-y-1/2 transition-transform hover:scale-150 z-20 shadow-[0_0_12px_rgba(6,182,212,0.8)]"
        title="Universal Input Target"
      />
      {/* 1. Directives / Persona Input Port */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-instructions"
        style={{ top: '18%' }}
        className="!bg-sky-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(56,189,248,0.8)]"
        title="Input: Persona / Instruction Prompt"
      />
      {/* 2. Customer Episodic Memory Vault Port */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-memory"
        style={{ top: '32%' }}
        className="!bg-purple-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(168,85,247,0.8)]"
        title="Input: Customer Episodic Memory Vault"
      />
      {/* 3. Knowledge Vault Port */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-knowledge"
        style={{ top: '46%' }}
        className="!bg-emerald-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(16,185,129,0.8)]"
        title="Input: Knowledge Vault & RAG Documents"
      />
      {/* 4. AI Web Scraper Port */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-scraper"
        style={{ top: '60%' }}
        className="!bg-indigo-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(99,102,241,0.8)]"
        title="Input: Scraper & Live Crawl Context"
      />
      {/* 5. RBAC & Security Port */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-security"
        style={{ top: '74%' }}
        className="!bg-amber-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(245,158,11,0.8)]"
        title="Input: Security Filters & Negative Guardrails"
      />
      {/* 6. Section Route & Multi-Context Switcher Port */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-section-route"
        style={{ top: '88%' }}
        className="!bg-indigo-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(99,102,241,0.8)]"
        title="Input: Section & Route Switcher Trigger"
      />

      {/* ── Right Side: Multi-Port Output Handles ─────────────────────── */}
      {/* Universal Source Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="out-core"
        className="!bg-cyan-400 !w-4 !h-4 !border-2 !border-slate-950 !-right-2 top-1/2 -translate-y-1/2 transition-transform hover:scale-150 z-20 shadow-[0_0_12px_rgba(6,182,212,0.8)]"
        title="Universal Output Source"
      />
      {/* 1. Action & Tool Execution Port */}
      <Handle
        type="source"
        position={Position.Right}
        id="out-actions"
        style={{ top: '22%' }}
        className="!bg-purple-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(168,85,247,0.8)]"
        title="Output: Action & Tool Dispatches"
      />
      {/* 1.5 Website Live Co-Pilot & Web Widget Port */}
      <Handle
        type="source"
        position={Position.Right}
        id="out-web-widget"
        style={{ top: '33%' }}
        className="!bg-cyan-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(6,182,212,0.8)]"
        title="Output: Website Live Co-Pilot & Web Widget"
      />
      {/* 2. WhatsApp / Notifications Port */}
      <Handle
        type="source"
        position={Position.Right}
        id="out-whatsapp"
        style={{ top: '44%' }}
        className="!bg-emerald-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(16,185,129,0.8)]"
        title="Output: WhatsApp & Lead Alerts"
      />
      {/* 2.5 Enterprise SIP & PBX Phone Calling Port */}
      <Handle
        type="source"
        position={Position.Right}
        id="out-sip-call"
        style={{ top: '55%' }}
        className="!bg-cyan-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(6,182,212,0.8)]"
        title="Output: Enterprise SIP & PBX Voice Gateway"
      />
      {/* 3. POS & Billing Port */}
      <Handle
        type="source"
        position={Position.Right}
        id="out-pos"
        style={{ top: '66%' }}
        className="!bg-emerald-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(16,185,129,0.8)]"
        title="Output: POS Billing & Payment Actions"
      />
      {/* 4. Custom Webhooks & APIs */}
      <Handle
        type="source"
        position={Position.Right}
        id="out-webhook"
        style={{ top: '80%' }}
        className="!bg-cyan-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(6,182,212,0.8)]"
        title="Output: External Webhooks & APIs"
      />
      {/* 5. Intent Alerts Port */}
      <Handle
        type="source"
        position={Position.Right}
        id="out-intent"
        style={{ top: '94%' }}
        className="!bg-rose-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(244,63,94,0.8)]"
        title="Output: Intent Alerts & Triggers"
      />

      {/* Top & Bottom Handles */}
      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!bg-cyan-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 transition-transform hover:scale-125"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="out-bottom"
        className="!bg-cyan-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-bottom-2 transition-transform hover:scale-125"
      />

      {/* ── Floating Cyber-HUD Action: View Live Data in Portal & WhatsApp Web (Fades in on Hover) ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0 flex items-center gap-2 whitespace-nowrap">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('whatsapp_web', id);
            }}
            className="nodrag flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-[10px] uppercase tracking-wider shadow-[0_0_20px_rgba(16,185,129,0.8)] hover:scale-105 transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95 border border-emerald-300/80"
            title="Open authentic WhatsApp Web & Mobile Console for live customer conversations & human takeover"
          >
            <span>💬 WHATSAPP WEB</span>
            <ArrowRight size={11} className="text-slate-950" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('lunaCoreNode', id);
            }}
            className="nodrag flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-cyan-950 via-slate-900 to-blue-950 border-2 border-cyan-400 text-cyan-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(6,182,212,0.8)] hover:border-cyan-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data tab to view processed messages, customer WhatsApp chats & token counts"
          >
            <Database size={13} className="text-cyan-300 animate-pulse" />
            <span>Portal Data</span>
            <ArrowRight size={12} className="text-cyan-400" />
          </button>
        </div>
      )}

      {/* ── Glowing Header with Cyber Reactor Core (Sovereign Magic Spec) ── */}
      <div className="bg-gradient-to-r from-cyan-950/90 via-[#0B101E]/95 to-blue-950/90 px-5 py-3.5 border-b border-white/[0.08] flex items-center justify-between rounded-t-3xl relative overflow-hidden shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1)]">
        {/* Animated Background Aura */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/15 via-teal-500/10 to-blue-500/15 animate-pulse pointer-events-none" />

        <div className="flex items-center gap-3 z-10 min-w-0">
          <div className="relative p-2.5 rounded-2xl bg-cyan-500/20 border border-cyan-400/50 text-cyan-300 shadow-[0_0_20px_rgba(6,182,212,0.5)] shrink-0">
            <Cpu size={22} className="animate-spin text-cyan-300 [animation-duration:12s]" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-black text-white tracking-wider uppercase font-sans whitespace-nowrap">
                Luna Core Hub
              </h3>
              <span className="flex items-center gap-1 text-[9px] font-mono font-bold text-emerald-400 bg-emerald-950/70 px-2 py-0.5 rounded-full border border-emerald-500/30 shrink-0">
                <Activity size={10} className="animate-pulse" /> LIVE CORE
              </span>
            </div>
            <p className="text-[10px] text-cyan-300/80 font-mono truncate">Central Multimodal Agent Brain</p>
          </div>
        </div>

        <div className="flex items-center gap-2 z-10 shrink-0">
          {saveIndicator && (
            <span className="text-[9px] font-mono font-bold text-emerald-400 animate-pulse bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/30">
              {saveIndicator}
            </span>
          )}
          <span className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-[0_0_12px_rgba(6,182,212,0.3)]">
            VPE HUB
          </span>
        </div>
      </div>

      {isCompactView ? (
        /* Executive Compact Glanceable Mode (Silicon Valley Standard) */
        <div className="p-4 space-y-3 text-xs">
          <div className="p-3 rounded-2xl bg-gradient-to-r from-cyan-950/60 via-slate-900/90 to-blue-950/60 border border-cyan-500/30 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-cyan-500/20 border border-cyan-400/40 flex items-center justify-center font-black text-cyan-300 text-sm shadow-[0_0_15px_rgba(6,182,212,0.3)]">
                  {nodeData.agentName ? nodeData.agentName.charAt(0).toUpperCase() : 'L'}
                </div>
                <div className="min-w-0">
                  <h4 className="font-bold text-white text-sm truncate">{nodeData.agentName || 'Luna AI'}</h4>
                  <p className="text-[10px] text-cyan-300 font-mono">Central Decision Core</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => openInspector && openInspector(id)}
                className="nodrag px-2.5 py-1 rounded-lg text-[10px] font-bold bg-cyan-500/20 hover:bg-cyan-500/35 text-cyan-200 border border-cyan-400/40 transition cursor-pointer active:scale-95 shadow-sm"
              >
                Inspect ⚙️
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5 pt-1">
              <span className="px-2 py-0.5 rounded-md bg-slate-800/90 border border-slate-700/80 text-[10px] font-mono text-cyan-300 font-semibold">
                🔊 {nodeData.baseVoice || 'Kore'}
              </span>
              <span className="px-2 py-0.5 rounded-md bg-slate-800/90 border border-slate-700/80 text-[10px] font-mono text-emerald-300 font-semibold">
                🌐 {nodeData.languageMode === 'bilingual' ? 'Bilingual' : nodeData.languageMode || 'Bilingual'}
              </span>
              <span className="px-2 py-0.5 rounded-md bg-slate-800/90 border border-slate-700/80 text-[10px] font-mono text-purple-300 font-semibold">
                🔥 Temp {nodeData.temperature ?? 0.7}
              </span>
              <button
                type="button"
                onClick={() => setIsCacheModalOpen(true)}
                className="nodrag px-2 py-0.5 rounded-md bg-cyan-950/70 hover:bg-cyan-900/80 border border-cyan-500/40 text-[10px] font-mono text-cyan-300 font-semibold transition cursor-pointer flex items-center gap-1 shadow-sm active:scale-95"
                title="Inspect compiled flow neural cache"
              >
                <Zap size={10} className="text-cyan-400" />
                <span>Cache &lt;0.05ms</span>
              </button>
              {nodeData.naturalSpokenSinhala !== false && (
                <span className="px-2 py-0.5 rounded-md bg-emerald-950/60 border border-emerald-500/30 text-[10px] font-mono text-emerald-400 font-semibold">
                  ⚡ Sinhala Cadence ON
                </span>
              )}
            </div>

            {openPortalData && (
              <div className="space-y-1.5 mt-1">
                <button
                  type="button"
                  onClick={() => openPortalData('whatsapp_web', id)}
                  className="nodrag w-full py-2 px-3 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-black text-xs flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 cursor-pointer active:scale-95 transition-all"
                  title="Launch authentic WhatsApp Web & Mobile Console for live customer chats & human takeover"
                >
                  <span>💬 Open WhatsApp Web</span>
                  <ArrowRight size={13} className="text-slate-950" />
                </button>
                <div className="flex items-center justify-between px-2.5 py-1 rounded-lg bg-emerald-950/40 border border-emerald-500/30 text-[10px]">
                  <span className="font-bold text-emerald-300 flex items-center gap-1">
                    <span>🛍️</span> Orders & Leads Filtered
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowDeleteChatsModal(true)}
                      className="nodrag text-[9px] font-bold text-rose-400 hover:text-rose-300 underline cursor-pointer flex items-center gap-0.5"
                      title="Delete WhatsApp chat history & purge Luna memory context"
                    >
                      <Trash2 size={9} />
                      <span>Purge</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => openPortalData('whatsapp_web', id)}
                      className="nodrag text-[9px] font-black uppercase text-emerald-300 hover:text-white underline cursor-pointer"
                    >
                      View →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="p-5 space-y-4 text-xs">
          {/* Sovereign Multimodal Decision Core HUD */}
          <div className="p-3 rounded-2xl bg-gradient-to-r from-cyan-950/70 via-slate-900/90 to-blue-950/70 border border-cyan-500/30 space-y-2">
            <div className="flex items-center justify-between text-[11px]">
            <span className="font-bold text-cyan-200 flex items-center gap-1.5 font-mono">
              <Cpu size={14} className="text-cyan-400" /> Sovereign Brain
            </span>
            <span className="font-mono text-[9px] font-bold text-emerald-400 bg-emerald-950/70 px-2 py-0.5 rounded border border-emerald-500/30">
              Live Preview Engine
            </span>
          </div>
          <p className="text-[10px] text-cyan-300/70 font-mono flex items-center justify-between border-t border-cyan-500/10 pt-1.5">
            <span>Jigsaw Graph Binding:</span>
            <span className="font-bold text-slate-200">
              {(() => {
                const edges = getEdges?.() || [];
                const inCount = edges.filter((e) => e.target === id).length;
                const outCount = edges.filter((e) => e.source === id).length;
                return `${inCount} Upstream Memory · ${outCount} Downstream Tools`;
              })()}
            </span>
          </p>
        </div>

        {/* ⚡ Luna Persistent Neural Cache & Token Engine HUD */}
        <div className="p-3 rounded-2xl bg-gradient-to-r from-cyan-950/80 via-slate-900/90 to-blue-950/80 border border-cyan-500/40 space-y-2.5 shadow-[0_0_20px_rgba(6,182,212,0.15)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-cyan-500/20 text-cyan-300 border border-cyan-400/40 shadow-[0_0_10px_rgba(6,182,212,0.3)]">
                <Zap size={13} className={`text-cyan-400 ${isNeuralCacheEnabled ? 'animate-pulse' : ''}`} />
              </div>
              <div>
                <span className="text-xs font-black text-white font-sans flex items-center gap-1.5">
                  Neural Cache & Token Engine
                </span>
                <p className="text-[9px] text-cyan-300/80 font-mono">
                  {isNeuralCacheEnabled ? 'Sub-millisecond memory & zero-lag recall' : 'Live graph traversal active (No cache)'}
                </p>
              </div>
            </div>

            {/* Interactive Toggle Button */}
            <button
              type="button"
              onClick={handleToggleNeuralCache}
              disabled={isTogglingCache}
              className={`nodrag text-[9px] font-mono font-bold px-2.5 py-1 rounded-full border flex items-center gap-1.5 transition-all cursor-pointer shadow-sm active:scale-95 ${
                isNeuralCacheEnabled
                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 shadow-[0_0_12px_rgba(16,185,129,0.3)] hover:bg-emerald-500/30'
                  : 'bg-amber-500/20 text-amber-300 border-amber-500/40 shadow-[0_0_12px_rgba(245,158,11,0.3)] hover:bg-amber-500/30'
              }`}
              title={
                isNeuralCacheEnabled
                  ? 'Neural Cache is ACTIVE. Sub-millisecond recall. Click to disable.'
                  : 'Neural Cache is DISABLED. Luna freshly live-reads all nodes on every message. Click to enable.'
              }
            >
              <span className={`w-1.5 h-1.5 rounded-full ${isNeuralCacheEnabled ? 'bg-emerald-400 animate-ping' : 'bg-amber-400'} inline-block`} />
              <span>{isTogglingCache ? '...' : (isNeuralCacheEnabled ? 'ENABLED (FAST)' : 'DISABLED (LIVE NODES)')}</span>
            </button>
          </div>

          <div className="flex items-center justify-between text-[9px] font-mono px-2.5 py-1 rounded-lg bg-slate-950/70 border border-white/5 text-slate-300">
            <span className={`flex items-center gap-1 ${isNeuralCacheEnabled ? 'text-cyan-300' : 'text-amber-300'}`}>
              <Activity size={10} className={isNeuralCacheEnabled ? 'text-cyan-400' : 'text-amber-400'} />
              {isNeuralCacheEnabled ? '< 0.05ms Read Latency' : 'Live Graph Dynamic Scan'}
            </span>
            <span className={isNeuralCacheEnabled ? 'text-slate-400' : 'text-amber-300/80 font-bold'}>
              {isNeuralCacheEnabled ? 'RAM In-Memory Cache' : 'Every Message Reads All Nodes'}
            </span>
          </div>

          <div className="flex items-center justify-between pt-1 border-t border-cyan-500/10 gap-1.5">
            <button
              type="button"
              onClick={() => setIsCacheModalOpen(true)}
              className="nodrag flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/35 border border-cyan-400/40 text-cyan-200 hover:text-white text-[10px] font-extrabold tracking-wide uppercase transition cursor-pointer active:scale-95 shadow-sm"
              title="Inspect compiled prompt and token telemetry modal"
            >
              <Eye size={12} className="text-cyan-300" />
              <span>Inspect</span>
            </button>

            {openPortalData && (
              <button
                type="button"
                onClick={() => openPortalData('neural_cache', id)}
                className="nodrag flex items-center justify-center gap-1 py-1.5 px-2.5 rounded-xl bg-gradient-to-r from-blue-600/30 to-indigo-600/30 hover:from-blue-600/50 hover:to-indigo-600/50 border border-indigo-400/40 text-indigo-200 hover:text-white text-[10px] font-extrabold tracking-wide uppercase transition cursor-pointer active:scale-95 shadow-sm"
                title="Open full-screen Neural Cache Studio in Portal Data tab"
              >
                <Maximize2 size={11} className="text-indigo-300" />
                <span>Studio</span>
              </button>
            )}

            <button
              type="button"
              onClick={handleQuickPurgeCache}
              disabled={isPurgingCache}
              className="nodrag flex items-center justify-center gap-1 py-1.5 px-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-white/10 text-slate-300 hover:text-white text-[10px] font-bold transition cursor-pointer active:scale-95 disabled:opacity-50"
              title="Purge cached state and re-index all nodes immediately"
            >
              <RefreshCw size={11} className={isPurgingCache ? 'animate-spin text-cyan-300' : 'text-slate-400'} />
              <span>{isPurgingCache ? '...' : (cachePurgeToast || 'Re-index')}</span>
            </button>
          </div>
        </div>
        
        {/* 1. Live Avatar Face Viewport & Selector */}
        <div className="p-3.5 rounded-2xl bg-slate-900/90 border border-cyan-500/30 shadow-inner flex items-center gap-4">
          <div className="relative w-16 h-16 rounded-2xl bg-slate-950/80 border border-cyan-500/40 flex items-center justify-center overflow-hidden shadow-[0_0_20px_rgba(6,182,212,0.3)] shrink-0 group">
            <LunaFace
              faceId={currentFaceId === 'custom_properbot' ? 'custom_properbot' : (isCustomFace ? 'custom_avatar' : currentFaceId)}
              customAvatarUrl={currentFaceId === 'custom_properbot' ? '/avatars/properbot/properbot_standing.png' : (isCustomFace ? (nodeData.customAvatarUrl || customAvatarUrl) : undefined)}
              isListening={false}
              isSpeaking={false}
              sentiment="neutral"
              className="w-14 h-14"
            />
            <div className="absolute inset-0 bg-cyan-400/5 pointer-events-none rounded-2xl" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <Sparkles size={12} className="text-cyan-400" /> Active Avatar Face
              </label>
              <button
                type="button"
                onClick={() => {
                  setModalDefaultTab('bespoke_avatar');
                  setIsRequestModalOpen(true);
                }}
                className="nodrag text-[9px] font-mono font-bold text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition cursor-pointer hover:underline"
                title="Request a custom 3D mascot or bespoke avatar for your company"
              >
                <Crown size={10} className="text-amber-400" />
                <span>Request Custom 3D</span>
              </button>
            </div>

            <select
              value={currentFaceId === 'custom_properbot' ? 'custom_properbot' : (isCustomFace ? 'custom_avatar' : currentFaceId)}
              onChange={(e) => {
                const newFace = e.target.value;
                if (newFace === 'custom_properbot') {
                  const updates = {
                    avatarFaceId: 'custom_properbot',
                    agentName: (nodeData.agentName && nodeData.agentName !== 'Luna AI' && nodeData.agentName !== 'Luna') ? nodeData.agentName : 'ProperBot',
                    customAvatarName: 'ProperBot',
                    customAvatarUrl: '/avatars/properbot/properbot_standing.png',
                  };
                  setNodes((nds) =>
                    nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...updates } } : n))
                  );
                  persistNodeDataDirectly('avatarFaceId', 'custom_properbot', updates);
                } else {
                  handleChange('avatarFaceId', newFace);
                }
              }}
              className="nodrag w-full bg-slate-950 border border-cyan-500/30 rounded-xl px-2.5 py-1.5 text-cyan-200 text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30 cursor-pointer shadow-inner truncate"
            >
              {(nodeData.customAvatarUrl || customAvatarUrl) && (
                <option value="custom_avatar" className="bg-slate-950 text-emerald-300 font-bold">
                  📸 [CUSTOM AVATAR] {nodeData.customAvatarName || customAvatarName || 'Custom Photo / Logo'}
                </option>
              )}
              {LUNA_FACE_CATALOG.filter((f: any) => {
                if (f.forTenantEmail) {
                  const isMatchingTenant =
                    tenant?.email === f.forTenantEmail ||
                    Number(tenant?.id) === Number(f.forTenantId) ||
                    String(tenant?.id) === String(f.forTenantId) ||
                    tenant?.name?.toLowerCase().includes('proper teck') ||
                    !isTenantView;
                  return isMatchingTenant;
                }
                return true;
              }).map((f) => (
                <option key={f.id} value={f.id} className="bg-slate-950 text-slate-100">
                  {f.badge ? `[${f.badge}] ` : '[OFFICIAL] '}{f.name}
                </option>
              ))}
            </select>

            {/* Photo / Logo Upload Action Bar */}
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <input
                ref={avatarFileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarFileUpload}
              />
              {canUploadAvatar ? (
                <button
                  type="button"
                  disabled={isUploadingAvatar}
                  onClick={() => avatarFileInputRef.current?.click()}
                  className="nodrag text-[9px] font-bold px-2 py-1 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/40 flex items-center gap-1 transition cursor-pointer active:scale-95 disabled:opacity-50"
                  title="Upload a custom photo or logo for your website widget avatar"
                >
                  {isUploadingAvatar ? (
                    <Loader2 size={10} className="animate-spin text-cyan-300" />
                  ) : (
                    <Upload size={10} className="text-cyan-400" />
                  )}
                  <span>{isUploadingAvatar ? 'Uploading...' : 'Upload Photo / Logo'}</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (openAccountModal) openAccountModal();
                    else {
                      setModalDefaultTab('bespoke_avatar');
                      setIsRequestModalOpen(true);
                    }
                  }}
                  className="nodrag text-[9px] font-mono font-bold px-2 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 flex items-center gap-1 transition cursor-pointer active:scale-95"
                >
                  <Lock size={9} />
                  <span>Unlock Custom Photo</span>
                </button>
              )}

              {(nodeData.customAvatarUrl || isCustomFace) && (
                <button
                  type="button"
                  onClick={() => {
                    handleChange('customAvatarUrl', '');
                    handleChange('avatarFaceId', 'cute_chibi_bot');
                    persistNodeDataDirectly('customAvatarUrl', '', { avatarFaceId: 'cute_chibi_bot' });
                  }}
                  className="nodrag text-[9px] text-slate-400 hover:text-rose-400 transition underline cursor-pointer"
                >
                  Reset to Default
                </button>
              )}
            </div>
            {avatarUploadError && (
              <p className="text-[9px] text-rose-400 mt-1">{avatarUploadError}</p>
            )}
            <p className="text-[9px] text-slate-400 mt-1 truncate">{activeFaceObj.tagline}</p>
          </div>
        </div>

        {/* 2. Agent Name & Base Voice Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Agent Master Name with White-Label Governance */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5 truncate">
                <Bot size={12} className="text-cyan-400 shrink-0" /> Agent Name
              </label>
              {!canRenameAgent && (
                <button
                  type="button"
                  onClick={() => {
                    if (openAccountModal) {
                      openAccountModal();
                    } else {
                      setModalDefaultTab('white_label_rename');
                      setIsRequestModalOpen(true);
                    }
                  }}
                  className="nodrag text-[8px] font-mono font-bold px-1.5 py-0.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 flex items-center gap-1 transition cursor-pointer shrink-0 active:scale-95"
                  title="Unlock custom agent naming & white-label branding"
                >
                  <Lock size={9} />
                  <span>UNLOCK</span>
                </button>
              )}
            </div>

            {canRenameAgent ? (
              <input
                type="text"
                className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs font-bold focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30 transition-all placeholder:text-slate-600 truncate"
                placeholder="e.g. Luna AI"
                value={nodeData.agentName !== undefined ? nodeData.agentName : (customAvatarName || 'Luna AI')}
                onChange={(e) => {
                  const val = e.target.value;
                  setNodes((nds) =>
                    nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, agentName: val, customAvatarName: val } } : n))
                  );
                  persistNodeDataDirectly('agentName', val, { customAvatarName: val });
                }}
              />
            ) : (
              <div
                onClick={() => {
                  if (openAccountModal) {
                    openAccountModal();
                  } else {
                    setModalDefaultTab('white_label_rename');
                    setIsRequestModalOpen(true);
                  }
                }}
                className="nodrag w-full bg-slate-950/90 border border-amber-500/30 hover:border-amber-400/60 rounded-xl px-3 py-2 text-slate-300 text-xs font-bold flex items-center justify-between cursor-pointer transition-all shadow-inner group"
                title="Click to unlock custom agent naming & white-label branding"
              >
                <span className="font-mono text-cyan-300 truncate">Luna AI</span>
                <span className="text-[9px] font-mono text-amber-400/80 group-hover:text-amber-300 flex items-center gap-1 shrink-0 ml-1">
                  <Lock size={10} />
                  <span>PRO REBRAND</span>
                </span>
              </div>
            )}
          </div>

          {/* Developer / Creator Attribution */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <Code2 size={12} className="text-emerald-400" /> Developer Attribution
              </label>
              <span className="text-[9px] font-mono text-emerald-400/80 bg-emerald-950/60 px-1.5 py-0.5 rounded border border-emerald-500/20">
                WHITE-LABEL
              </span>
            </div>

            {canRenameAgent ? (
              <input
                type="text"
                className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs font-bold focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400/30 transition-all placeholder:text-slate-600 truncate"
                placeholder="e.g. Sithumina.lk Tech Team"
                value={nodeData.customDeveloperName || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setNodes((nds) =>
                    nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, customDeveloperName: val } } : n))
                  );
                  persistNodeDataDirectly('customDeveloperName', val, { customDeveloperName: val });
                }}
              />
            ) : (
              <div
                onClick={() => {
                  if (openAccountModal) {
                    openAccountModal();
                  } else {
                    setModalDefaultTab('white_label_rename');
                    setIsRequestModalOpen(true);
                  }
                }}
                className="nodrag w-full bg-slate-950/90 border border-amber-500/30 hover:border-amber-400/60 rounded-xl px-3 py-2 text-slate-300 text-xs font-bold flex items-center justify-between cursor-pointer transition-all shadow-inner group"
                title="Click to unlock custom developer attribution & white-label branding"
              >
                <span className="font-mono text-emerald-300 truncate">Kangavena Cloud</span>
                <span className="text-[9px] font-mono text-amber-400/80 group-hover:text-amber-300 flex items-center gap-1 shrink-0 ml-1">
                  <Lock size={10} />
                  <span>PRO REBRAND</span>
                </span>
              </div>
            )}
          </div>

          {/* Base Voice Preset */}
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1.5">
              <Mic size={12} className="text-cyan-400" /> Base Voice
            </label>
            <select
              value={currentVoice}
              onChange={(e) => handleChange('baseVoice', e.target.value)}
              className="nodrag w-full bg-slate-900/90 border border-white/10 rounded-xl px-2.5 py-2 text-slate-100 text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30 cursor-pointer"
            >
              {BASE_VOICES.map((v) => (
                <option key={v.id} value={v.id} className="bg-slate-950 text-slate-100">
                  {v.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 3. Language Mode Selector */}
        <div>
          <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1.5">
            <Globe size={12} className="text-cyan-400" /> Language Operation Mode
          </label>
          <select
            value={currentLang}
            onChange={(e) => handleChange('languageMode', e.target.value)}
            className="nodrag w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30 cursor-pointer"
          >
            {LANGUAGE_MODES.map((l) => (
              <option key={l.id} value={l.id} className="bg-slate-900 text-slate-100">
                {l.name} — {l.desc}
              </option>
            ))}
          </select>
        </div>

        {/* 🗣️ Natural Spoken Sinhala Toggle (කතා කරන භාෂාවෙන්) */}
        <div className="p-2.5 rounded-xl bg-gradient-to-r from-emerald-950/40 via-teal-950/30 to-slate-900/60 border border-emerald-500/30 space-y-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 shrink-0">
                <Mic size={13} className="animate-pulse" />
              </div>
              <div>
                <span className="text-xs font-black text-white font-sans flex items-center gap-1.5">
                  Natural Spoken Sinhala
                  <span className="text-[8px] font-mono font-bold bg-emerald-500/20 text-emerald-300 px-1 py-0.5 rounded border border-emerald-500/30">
                    කතා කරන භාෂාව
                  </span>
                </span>
                <p className="text-[10px] text-slate-300/80 font-sans leading-tight">
                  Colloquial everyday Sri Lankan Sinhala with zero rigid bookish written text & no foreign accents.
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-2">
              <input
                type="checkbox"
                checked={nodeData.naturalSpokenSinhala !== false}
                onChange={(e) => handleChange('naturalSpokenSinhala', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
            </label>
          </div>
        </div>

        {/* 4. Default Copywriting & Response Format Style */}
        <div>
          <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1.5">
            <Sparkles size={12} className="text-cyan-400" /> Copywriting & Delivery Style
          </label>
          <select
            value={nodeData.responseStyle || 'sales_promo'}
            onChange={(e) => handleChange('responseStyle', e.target.value)}
            className="nodrag w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30 cursor-pointer font-medium"
          >
            <option value="sales_promo" className="bg-slate-900 text-slate-100">🔥 High-Converting Sales Copy & Promo (Prices, Emojis, Bullet Points)</option>
            <option value="business_corporate" className="bg-slate-900 text-slate-100">🏢 Professional Corporate & Clean Executive</option>
            <option value="friendly_concierge" className="bg-slate-900 text-slate-100">💬 Friendly Conversational & Warm Concierge</option>
            <option value="technical_expert" className="bg-slate-900 text-slate-100">🔬 Technical & Deep Specification Details</option>
            <option value="direct_short" className="bg-slate-900 text-slate-100">⚡ Direct, Fast & Ultra-Short Bullet Points</option>
          </select>
        </div>

        {/* 4. Temperature / Creativity Slider */}
        <div className="pt-2 border-t border-white/5 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <Sliders size={12} className="text-cyan-400" /> AI Temperature / Creativity
            </span>
            <span className="text-[10px] font-mono font-bold text-cyan-300 bg-cyan-950/80 px-2 py-0.5 rounded-md border border-cyan-500/30">
              {currentTemp.toFixed(2)}
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={currentTemp}
            onChange={(e) => handleChange('temperature', parseFloat(e.target.value))}
            className="nodrag nowheel w-full h-1.5 bg-slate-900 rounded-lg appearance-none cursor-pointer accent-cyan-400"
          />
        </div>

        {/* 5. Human Handover & Live Takeover Engine */}
        <div className="p-3 rounded-2xl bg-slate-950/80 border border-cyan-500/20 space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black uppercase tracking-wider text-cyan-300 flex items-center gap-1.5">
              <Headphones size={12} className="text-cyan-400" /> Human Handover & Takeover
            </span>
            <span className="text-[8px] font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
              PORTAL SYNCED
            </span>
          </div>

          <div className="flex items-center justify-between text-[11px] text-slate-300">
            <span className="flex items-center gap-1">
              <UserCheck size={12} className="text-emerald-400" /> Auto-Detect Human Requests
            </span>
            <input
              type="checkbox"
              checked={nodeData.enableHumanHandoverAlert !== false}
              onChange={(e) => handleChange('enableHumanHandoverAlert', e.target.checked)}
              className="nodrag w-4 h-4 rounded bg-slate-900 border-slate-700 text-cyan-500 focus:ring-cyan-400/30 cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-[9px] font-mono text-slate-400 mb-1 flex items-center gap-1">
              <PhoneCall size={10} className="text-amber-400" /> Owner WhatsApp Alert # (e.g. 94787114888)
            </label>
            <input
              type="text"
              value={nodeData.ownerAlertPhone || ''}
              onChange={(e) => handleChange('ownerAlertPhone', e.target.value)}
              placeholder="e.g. 94787114888"
              className="nodrag nowheel w-full bg-slate-900 border border-white/10 rounded-xl px-2.5 py-1.5 text-slate-100 text-xs font-mono focus:outline-none focus:border-cyan-400"
            />
          </div>
        </div>

        {/* ── Captured Orders & Leads Quick Hub ── */}
        {openPortalData && (
          <div className="p-3 rounded-2xl bg-gradient-to-r from-emerald-950/60 via-slate-900/90 to-teal-950/60 border border-emerald-500/30 flex items-center justify-between gap-2 shadow-lg">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-base shadow-sm">
                🛍️
              </div>
              <div>
                <p className="text-[11px] font-bold text-emerald-300">Order & Lead Captured Chats</p>
                <p className="text-[9px] text-slate-400">Track real-time conversions across WhatsApp & Web</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setShowDeleteChatsModal(true)}
                className="nodrag px-2.5 py-1.5 rounded-xl bg-rose-950/50 hover:bg-rose-900/70 border border-rose-500/30 text-rose-300 font-bold text-[10px] flex items-center gap-1 transition cursor-pointer active:scale-95"
                title="Delete WhatsApp conversation logs & purge active memory context"
              >
                <Trash2 size={11} className="text-rose-400" />
                <span>Purge Chats</span>
              </button>
              <button
                type="button"
                onClick={() => openPortalData('whatsapp_web', id)}
                className="nodrag px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-[10px] tracking-wide uppercase flex items-center gap-1.5 shadow-md shadow-emerald-500/20 active:scale-95 transition-all cursor-pointer"
              >
                <span>View Chats</span>
                <ArrowRight size={11} className="text-slate-950" />
              </button>
            </div>
          </div>
        )}

        {/* Clear Chat Status Toast */}
        {clearChatStatus && (
          <div className="p-2 rounded-xl bg-emerald-950/90 border border-emerald-500/50 text-emerald-300 text-[11px] font-semibold animate-in fade-in">
            {clearChatStatus}
          </div>
        )}

        {/* ── Port Mapping Legend Indicators ───────────────────────── */}
        <div className="p-3 rounded-2xl bg-slate-900/60 border border-white/5 space-y-2">
          <p className="text-[9px] text-slate-400 font-mono font-bold uppercase tracking-wider flex items-center justify-between">
            <span>⚡ Explicit Graph Wiring Ports</span>
            <span className="text-cyan-400">Zero-Trust Hub</span>
          </p>
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div className="flex flex-col gap-1 border-r border-white/5 pr-2">
              <span className="text-slate-400 font-bold flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 inline-block" /> Inputs (Left):
              </span>
              <span className="text-[9px] text-slate-400">Persona, Knowledge, Scraper, RBAC</span>
            </div>
            <div className="flex flex-col gap-1 pl-1">
              <span className="text-slate-400 font-bold flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block" /> Outputs (Right):
              </span>
              <span className="text-[9px] text-slate-400">Actions, WhatsApp, POS, Webhook</span>
            </div>
          </div>
        </div>
      </div>
      )}

        {/* ── Bespoke 3D Avatar & White-Label Request Modal ── */}
        <BespokeAvatarRequestModal
          isOpen={isRequestModalOpen}
          onClose={() => setIsRequestModalOpen(false)}
          authToken={authToken}
          tenantName={tenant?.name || 'Your Business'}
          defaultRequestType={modalDefaultTab}
        />

        {/* ── Luna Neural Context Cache & Token Engine Inspector Modal ── */}
        <LunaCacheInspectionModal
          isOpen={isCacheModalOpen}
          onClose={() => setIsCacheModalOpen(false)}
          tenantId={tenant?.id || 'global'}
          authToken={authToken}
          tenantApiKey={tenantApiKey}
          channel="default"
          onCachePurged={() => {
            setSaveIndicator('Cache Re-indexed');
            setTimeout(() => setSaveIndicator(null), 2500);
          }}
        />

        {/* ── Clear All WhatsApp Chats Confirmation Modal ── */}
        {showDeleteChatsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 animate-in fade-in duration-150">
            <div className="w-full max-w-md bg-slate-900 border border-rose-500/40 rounded-2xl shadow-2xl p-5 space-y-4 text-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-500/20 border border-rose-500/50 flex items-center justify-center text-rose-400 shrink-0">
                  <Trash2 size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-bold text-white">Purge WhatsApp Context & Chat Logs?</h4>
                  <p className="text-[11px] text-slate-400">Node: Luna Core Hub</p>
                </div>
              </div>

              <div className="bg-rose-950/30 border border-rose-500/30 rounded-xl p-3 space-y-1.5 text-xs text-rose-200">
                <p className="font-bold flex items-center gap-1 text-rose-300">
                  <span>⚠️ Reset WhatsApp Memory Context</span>
                </p>
                <p className="text-slate-300 text-[11px] leading-relaxed">
                  This will permanently delete all customer WhatsApp conversation records from the database and wipe Luna AI&apos;s active conversation memory context.
                </p>
              </div>

              <div className="flex items-center justify-between gap-2 pt-2 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteChatsModal(false);
                    openPortalData?.('whatsapp_web', id);
                  }}
                  className="text-[11px] text-emerald-400 hover:text-emerald-300 underline font-semibold"
                >
                  Manage individual chats →
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={isClearingChats}
                    onClick={() => setShowDeleteChatsModal(false)}
                    className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-300 transition cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isClearingChats}
                    onClick={handleClearAllWhatsAppChats}
                    className="px-3.5 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-xs font-bold text-white transition flex items-center gap-1.5 cursor-pointer shadow-lg shadow-rose-600/30 disabled:opacity-50"
                  >
                    {isClearingChats ? (
                      <>
                        <Loader2 size={13} className="animate-spin" />
                        <span>Purging...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 size={13} />
                        <span>Yes, Purge</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
});

LunaCoreNode.displayName = 'LunaCoreNode';
export default LunaCoreNode;
